import React, { useState, useRef } from "react";
import { Plus, Image as ImageIcon, X, Package, Check, Upload, ArrowLeft, Trash2, Pencil } from "lucide-react";
import { Button } from "../../../Components/UI/button";
import { Input } from "../../../Components/UI/input";
import { Label } from "../../../Components/UI/label";
import { Textarea } from "../../../Components/UI/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "../../../Components/UI/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
} from "../../../Components/UI/drawer";
import { AlertBox } from "../../../Components/Common/AlertBox";
import ImageCropperDialog from "../../../Components/Common/ImageCropper";
import { compressImage } from "../../../Utils/ImageCompressor";
import apiService from "../../../Services/APIService";
import { getUploadUrl } from "../../../Utils/Utilities";
import useIsMobile from "../../../Hooks/useIsMobile";
import { SpeechControls } from "../../../Components/Common/SpeechControls";
import { toast } from "sonner";

export const resolveCatalogImageUrl = (rawImg) => {
  if (!rawImg) return null;
  if (typeof rawImg !== "string") return null;
  const trimmed = rawImg.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
  const cleanPath = trimmed.replace(/^\/+/, "");
  if (cleanPath.toLowerCase().startsWith("uploads/")) {
    return getUploadUrl(cleanPath);
  }
  return getUploadUrl(`Uploads/${cleanPath}`);
};

const WebStoreCatalog = ({
  catalog = [],
  onAddProduct,
  onUpdateProduct,
  onDeleteProduct,
  isSaving = false,
}) => {
  const isMobile = useIsMobile(768);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Form states
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [selectedFileForCrop, setSelectedFileForCrop] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fileInputRef = useRef(null);

  const resetForm = () => {
    setEditingProduct(null);
    setName("");
    setDescription("");
    setImageFile(null);
    setImagePreviewUrl(null);
    setSelectedFileForCrop(null);
    setSubmitting(false);
    setDeleting(false);
    setDeleteAlertOpen(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleOpenAdd = () => {
    if (catalog.length >= 10) {
      toast.error("Maximum 10 products can be added to your catalog.");
      return;
    }
    resetForm();
    setIsAddOpen(true);
  };

  const handleOpenEdit = (item) => {
    const rawImg = item.PImg || item.pImg || item.Img || item.img || item.imageUrl || "";
    const imageUrl = resolveCatalogImageUrl(rawImg);

    resetForm();
    setEditingProduct(item);
    setName(item.PNm || item.pNm || item.name || "");
    setDescription(item.PTxt || item.pTxt || item.description || "");
    setImageFile(null);
    setImagePreviewUrl(imageUrl);
    setIsAddOpen(true);
  };

  const handleCloseAdd = () => {
    resetForm();
    setIsAddOpen(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
      ];
      if (allowedTypes.includes(file.type) || file.type.startsWith("image/")) {
        setSelectedFileForCrop(file);
      } else {
        toast.error("Please select a valid image format (JPEG, PNG, WebP)");
      }
      e.target.value = "";
    }
  };

  const handleClearImage = (e) => {
    e?.stopPropagation();
    setImageFile(null);
    setImagePreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleCropComplete = async (croppedFile) => {
    try {
      const compressed = await compressImage(croppedFile, 1200, 0.75);
      if (compressed.size > 2 * 1024 * 1024) {
        toast.error("File size should be less than 2 MB.");
        setSelectedFileForCrop(null);
        return;
      }
      const objectUrl = URL.createObjectURL(compressed);
      setImageFile(compressed);
      setImagePreviewUrl(objectUrl);
    } catch (err) {
      console.error("Image compression error:", err);
      if (croppedFile.size > 2 * 1024 * 1024) {
        toast.error("File size should be less than 2 MB.");
        setSelectedFileForCrop(null);
        return;
      }
      const objectUrl = URL.createObjectURL(croppedFile);
      setImageFile(croppedFile);
      setImagePreviewUrl(objectUrl);
    }
    setSelectedFileForCrop(null);
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error("Please enter a product or service name.");
      return;
    }

    if (!imagePreviewUrl && !imageFile) {
      toast.error("Please add an image for this product.");
      return;
    }

    if (imageFile && imageFile.size > 2 * 1024 * 1024) {
      toast.error("File size should be less than 2 MB.");
      return;
    }

    setSubmitting(true);
    try {
      let relativeImageUrl = "";

      // 1. Upload compressed image file if a new file was chosen
      if (imageFile) {
        let fileToUpload = imageFile;
        if (fileToUpload instanceof File) {
          fileToUpload = await compressImage(fileToUpload, 1200, 0.75);
          const ext = (fileToUpload.name || "").split(".").pop().toLowerCase() || "jpg";
          const safeExt = ["jpg", "jpeg", "png", "webp"].includes(ext) ? ext : "jpg";
          const baseName = (fileToUpload.name || "catalog")
            .replace(/\.[^/.]+$/, "")
            .replace(/[^a-zA-Z0-9_-]/g, "_")
            .substring(0, 30);
          // Prepend a unique timestamp & token so backend ExtUtilities never deletes other products' files
          const uniquePrefix = `cat_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
          const cleanFileName = `${uniquePrefix}_${baseName}.${safeExt}`;
          fileToUpload = new File([fileToUpload], cleanFileName, {
            type: fileToUpload.type || "image/jpeg",
          });
        }

        const uploadResult = await apiService.uploadFile(
          fileToUpload,
          "Catalog",
          "both"
        );

        if (uploadResult?.error) {
          toast.error(uploadResult.message || "Failed to upload image.");
          setSubmitting(false);
          return;
        }

        relativeImageUrl =
          uploadResult?.data?.relativePath ||
          uploadResult?.data?.fileName ||
          uploadResult?.relativePath ||
          uploadResult?.fileName ||
          "";
      } else if (editingProduct) {
        relativeImageUrl =
          editingProduct.PImg ||
          editingProduct.pImg ||
          editingProduct.Img ||
          editingProduct.img ||
          editingProduct.imageUrl ||
          "";
      }

      if (editingProduct) {
        // Update existing product
        const updatePayload = {
          pid: editingProduct.PID || editingProduct.id,
          name: trimmedName,
          imageUrl: relativeImageUrl,
          description: description.trim(),
          typeID: 1,
          typeName: "Product",
        };

        if (onUpdateProduct) {
          const res = await onUpdateProduct(updatePayload);
          if (res?.error) {
            toast.error(res.error);
            setSubmitting(false);
            return;
          }
        }
        toast.success("Product updated successfully.");
      } else {
        // Add new product
        const newProductPayload = {
          name: trimmedName,
          imageUrl: relativeImageUrl,
          description: description.trim(),
          typeID: 1,
          typeName: "Product",
        };

        if (onAddProduct) {
          const res = await onAddProduct(newProductPayload);
          if (res?.error) {
            toast.error(res.error);
            setSubmitting(false);
            return;
          }
        }
        toast.success("Product added to catalog.");
      }

      handleCloseAdd();
    } catch (err) {
      console.error("Error saving product to catalog:", err);
      toast.error(err?.message || "Failed to save product.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!editingProduct) return;
    const pid = editingProduct.PID || editingProduct.id;
    setDeleting(true);
    try {
      if (onDeleteProduct) {
        const res = await onDeleteProduct(pid);
        if (res?.error) {
          toast.error(res.error);
          setDeleting(false);
          return;
        }
      }
      toast.success("Product removed from catalog.");
      setDeleteAlertOpen(false);
      handleCloseAdd();
    } catch (err) {
      console.error("Error deleting product:", err);
      toast.error(err?.message || "Failed to delete product.");
    } finally {
      setDeleting(false);
    }
  };

  // Form content shared between desktop Dialog and mobile Drawer
  const renderProductForm = () => (
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
      {/* Scrollable Form Body */}
      <div className="px-4 py-3 pb-0 sm:px-4 sm:py-0 space-y-4 max-h-[calc(82vh-120px)] overflow-y-auto custom-scroll">
        {/* Product Image Field (Top) */}
        <div className="space-y-2">
          <Label required={true} className="text-sm font-medium text-gray-700">
            Product Image
          </Label>
          <input
            type="file"
            ref={fileInputRef}
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handleFileChange}
            className="hidden"
          />

          {imagePreviewUrl ? (
            <div
              onClick={() => fileInputRef.current?.click()}
              className="relative w-40 h-40 sm:w-44 sm:h-44 aspect-square rounded-xl border-2 border-zinc-200 hover:border-zinc-400 overflow-hidden group bg-zinc-50 cursor-pointer shadow-xs transition-all flex items-center justify-center"
              title="Click to change image"
            >
              <img
                src={imagePreviewUrl}
                alt="Product preview"
                className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-200"
              />

              {/* Hover overlay hint */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors pointer-events-none" />

              {/* Floating edit pencil icon (bottom-right) */}
              <div
                className="absolute bottom-2 right-2 h-7 w-7 bg-white/95 hover:bg-white text-zinc-700 hover:text-zinc-900 rounded-full shadow-md border border-zinc-200/90 flex items-center justify-center transition-transform group-hover:scale-105 active:scale-95 z-10"
                title="Change image"
              >
                <Pencil className="h-3.5 w-3.5" />
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="relative w-40 h-40 sm:w-44 sm:h-44 aspect-square rounded-xl bg-zinc-50/60 hover:bg-zinc-100 flex flex-col items-center justify-center gap-1.5 cursor-pointer transition-all shadow-xs group"
              title="Upload photo"
            >
              <svg className="absolute inset-0 w-full h-full pointer-events-none rounded-xl" xmlns="http://www.w3.org/2000/svg">
                <rect
                  x="1.5"
                  y="1.5"
                  width="calc(100% - 3px)"
                  height="calc(100% - 3px)"
                  rx="10"
                  ry="10"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeDasharray="5 4"
                  className="text-zinc-300 group-hover:text-zinc-400 transition-colors"
                />
              </svg>
              <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white flex items-center justify-center shadow-xs group-hover:scale-105 transition-transform z-1">
                <Upload className="h-4 w-4 sm:h-4.5 sm:w-4.5 text-zinc-500" />
              </div>
              <span className="text-xs font-medium text-[var(--text-color)] z-1">
                Upload Photo
              </span>
              <span className="text-[10px] text-[var(--sub-text-color)] z-1">
                1:1 square &bull; Max 2MB
              </span>
            </button>
          )}
        </div>

        {/* Product Name */}
        <div className="space-y-2 pb-1">
          <Label htmlFor="catalog-prd-name" required={true} className="text-sm font-medium text-gray-700">
            Product Name
          </Label>
          <div className="relative">
            <Input
              id="catalog-prd-name"
              type="text"
              value={name}
              maxLength={30}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Industrial Brass Valve"
              className="h-11 text-sm border-2 border-gray-300 hover:border-gray-400 focus:border-sahi-primary-color transition-all duration-200 w-full rounded-md px-3"
              autoFocus={!isMobile}
              autoComplete="off"
            />
            {name.length >= 25 && (
              <span className="absolute right-0 -bottom-4.5 text-[11px] text-[var(--sub-text-color)] pointer-events-none select-none">
                {name.length}/30
              </span>
            )}
          </div>
        </div>

        {/* Description */}
        <div className="space-y-2 pb-1">
          <div className="flex items-center justify-between">
            <Label htmlFor="catalog-prd-desc" className="text-sm font-medium text-gray-700">
              Description
            </Label>
          </div>
          <div className="relative">
            <Textarea
              id="catalog-prd-desc"
              rows={3}
              maxLength={200}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. High precision industrial valve for fluid control."
              className="min-h-[90px] text-sm border-2 border-gray-300 hover:border-gray-400 focus:border-sahi-primary-color transition-all duration-200 rounded-md w-full px-3 py-2 resize-none pr-20"
              autoComplete="off"
            />
            <SpeechControls
              targetId="catalog-prd-desc"
              value={description}
              onChange={(val) => setDescription(val)}
              isMobile={isMobile}
            />
            {description.length >= 195 && (
              <span className="absolute right-0 -bottom-4.5 text-[11px] text-[var(--sub-text-color)] pointer-events-none select-none">
                {description.length}/200
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Form Action Buttons (Left-aligned desktop, full-width mobile) */}
      <div className="p-4 border-t border-gray-100 shrink-0">
        <div className={`w-full flex gap-3 ${isMobile ? "flex-col" : "sm:flex-row justify-start"}`}>
          <Button
            type="submit"
            disabled={submitting || isSaving || !name.trim()}
            className={`sahi-primary-button ${isMobile ? "w-full" : "sm:flex-none sm:min-w-[140px]"} flex items-center justify-center gap-1.5 cursor-pointer`}
          >
            <Check className="h-4 w-4" strokeWidth={3} />
            {submitting || isSaving
              ? "Saving..."
              : editingProduct
                ? "Update Product"
                : "Save Product"}
          </Button>
          {!isMobile && (
            <Button
              type="button"
              variant="outline"
              onClick={handleCloseAdd}
              disabled={submitting || isSaving}
              className="sahi-secondary-button sm:flex-none sm:min-w-[100px] flex items-center justify-center gap-1.5 cursor-pointer"
            >
              <X className="w-3 h-3" />
              Close
            </Button>
          )}
        </div>
      </div>
    </form>
  );

  return (
    <div className="pt-2">
      {/* Grid of Catalog Cards & Add Product Card (5 per row on desktop) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6 w-full">
        {/* Render each existing catalog item */}
        {catalog.map((item, idx) => {
          const rawImg = item.PImg || item.pImg || item.Img || item.img || item.imageUrl || "";
          const imageUrl = resolveCatalogImageUrl(rawImg);
          const name = item.PNm || item.pNm || item.name || "Product";

          return (
            <div
              key={item.PID || idx}
              onClick={() => handleOpenEdit(item)}
              className="flex flex-col items-center gap-1.5 select-none group cursor-pointer w-full"
              title={`Click to edit ${name}`}
            >
              {/* Product Card */}
              <div className="relative w-full aspect-square rounded-xl overflow-hidden border border-zinc-200 bg-white shadow-xs group-hover:border-zinc-400 group-hover:shadow-md transition-all flex items-center justify-center">
                {imageUrl ? (
                  <>
                    <img
                      src={imageUrl}
                      alt={name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.style.display = "none";
                        if (e.target.nextElementSibling) {
                          e.target.nextElementSibling.style.display = "flex";
                        }
                      }}
                    />
                    <div className="hidden w-full h-full items-center justify-center bg-zinc-100 text-zinc-400">
                      <ImageIcon className="w-8 h-8 opacity-40" />
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full p-2 flex flex-col items-center justify-center bg-zinc-50 text-zinc-400">
                    <Package className="w-8 h-8 opacity-40" />
                    <span className="text-[10px] mt-1 text-zinc-400">No Image</span>
                  </div>
                )}
              </div>

              {/* Title Below (No description) */}
              <div className="text-center w-full px-0.5">
                <span
                  className="block text-sm text-[var(--text-color)] font-semibold truncate"
                  title={name}
                >
                  {name}
                </span>
              </div>
            </div>
          );
        })}

        {/* Add Product Card - Only shown if fewer than 10 products */}
        {catalog.length < 10 && (
          <div className="flex flex-col items-center gap-1.5 w-full">
            <button
              type="button"
              onClick={handleOpenAdd}
              className="relative w-full aspect-square rounded-xl bg-zinc-50/60 hover:bg-zinc-100 text-zinc-700 cursor-pointer shadow-xs active:scale-95 group flex flex-col items-center justify-center transition-all p-2"
            >
              <svg className="absolute inset-0 w-full h-full pointer-events-none rounded-xl" xmlns="http://www.w3.org/2000/svg">
                <rect
                  x="1.5"
                  y="1.5"
                  width="calc(100% - 3px)"
                  height="calc(100% - 3px)"
                  rx="10"
                  ry="10"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeDasharray="5 4"
                  className="text-zinc-300 group-hover:text-zinc-400 transition-colors"
                />
              </svg>
              <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-xs mb-1.5 z-1">
                <Plus className="w-4 h-4 text-zinc-600" />
              </div>
              <span className="text-xs font-semibold text-[var(--text-color)] z-1 text-center">
                Add Product
              </span>
            </button>
            <span className="text-xs text-transparent select-none">
              placeholder
            </span>
          </div>
        )}
      </div>

      {/* Image Cropper Modal */}
      {selectedFileForCrop && (
        <ImageCropperDialog
          isOpen={Boolean(selectedFileForCrop)}
          onClose={() => setSelectedFileForCrop(null)}
          file={selectedFileForCrop}
          onCropComplete={handleCropComplete}
          title="Crop Product Image"
          aspect={1}
        />
      )}

      {/* Desktop View: Radix Dialog */}
      {!isMobile && (
        <Dialog open={isAddOpen} onOpenChange={(open) => !open && handleCloseAdd()}>
          <DialogContent className="sm:max-w-md w-full p-0 overflow-hidden">
            <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
              <div className="sm:hidden flex items-center justify-between w-full h-8">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <DialogClose
                    tabIndex={-1}
                    className="p-0 w-auto h-auto flex-shrink-0 flex justify-center"
                    onClick={handleCloseAdd}
                  >
                    <ArrowLeft className="h-6 w-6" />
                    <span className="sr-only">Back</span>
                  </DialogClose>
                  <DialogTitle className="text-lg font-medium">
                    <div className="truncate">{editingProduct ? "Edit Product" : "Product Entry"}</div>
                  </DialogTitle>
                </div>
                {editingProduct && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 p-0 text-text-color hover:bg-gray-200 flex items-center justify-center cursor-pointer"
                    onClick={() => setDeleteAlertOpen(true)}
                    title="Delete Product"
                  >
                    <Trash2 className="h-5 w-5" />
                    <span className="sr-only">Delete</span>
                  </Button>
                )}
              </div>
              <DialogTitle className="hidden sm:block text-lg md:text-xl">
                {editingProduct ? "Edit Product" : "Product Entry"}
              </DialogTitle>
              <div className="hidden sm:flex items-center gap-1.5">
                {editingProduct && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 p-0 sm:text-text-color sm:hover:bg-gray-200 flex items-center justify-center cursor-pointer"
                    tabIndex={-1}
                    onClick={() => setDeleteAlertOpen(true)}
                    title="Delete Product"
                  >
                    <Trash2 className="h-5 w-5" />
                    <span className="sr-only">Delete</span>
                  </Button>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 p-0 sm:text-text-color sm:hover:bg-gray-200 flex items-center justify-center cursor-pointer"
                  tabIndex={-1}
                  onClick={handleCloseAdd}
                >
                  <X className="h-5 w-5" />
                  <span className="sr-only">Close</span>
                </Button>
              </div>
            </DialogHeader>
            {renderProductForm()}
          </DialogContent>
        </Dialog>
      )}

      {/* Mobile View: Vaul Drawer */}
      {isMobile && (
        <Drawer open={isAddOpen} onOpenChange={(open) => !open && handleCloseAdd()} shouldScaleBackground={false}>
          <DrawerContent className="p-0 [&>div:first-child]:hidden !top-auto !bottom-0 !h-auto max-h-[85dvh] rounded-t-[22px] border-t border-[var(--border)] z-[10002] overflow-hidden bg-background">
            <DrawerHeader className="px-4 py-3 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <DrawerClose asChild>
                  <button
                    type="button"
                    className="p-0 w-auto h-auto flex-shrink-0 flex justify-center text-gray-500 hover:text-gray-800 cursor-pointer"
                    onClick={handleCloseAdd}
                  >
                    <ArrowLeft className="h-6 w-6" />
                    <span className="sr-only">Back</span>
                  </button>
                </DrawerClose>
                <DrawerTitle className="text-lg font-medium text-text-color truncate tracking-normal">
                  {editingProduct ? "Edit Product" : "Product Entry"}
                </DrawerTitle>
              </div>
              <div className="flex items-center gap-1.5">
                {editingProduct && (
                  <button
                    type="button"
                    data-vaul-no-drag
                    className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-200 transition-colors cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteAlertOpen(true);
                    }}
                    title="Delete Product"
                  >
                    <Trash2 className="h-5 w-5" />
                    <span className="sr-only">Delete</span>
                  </button>
                )}
              </div>
            </DrawerHeader>
            <div className="overflow-y-auto flex-1 custom-scroll">
              {renderProductForm()}
            </div>
          </DrawerContent>
        </Drawer>
      )}

      {/* Unified Delete Confirmation AlertBox matching Offerings in Master */}
      <AlertBox
        open={deleteAlertOpen}
        onOpenChange={setDeleteAlertOpen}
        className="!z-[10021]"
        hideFooterBorder={true}
        title="Delete Product?"
        description={`Are you sure you want to delete "${name || editingProduct?.PNm || editingProduct?.name || 'this product'}"? This action cannot be undone.`}
        actions={[
          {
            label: 'Close',
            value: 'cancel',
            variant: 'cancel',
            className: 'sahi-secondary-button flex-1 sm:flex-none sm:min-w-[100px]',
            icon: <X className="w-3 h-3" />
          },
          {
            label: 'Delete Product',
            value: 'delete',
            variant: 'action',
            className: 'sahi-primary-button flex-1 sm:flex-none sm:min-w-[140px]',
            icon: <Trash2 className="h-4 w-4" />
          },
        ]}
        onAction={(action) => {
          if (action === 'delete') {
            handleDelete();
          } else {
            setDeleteAlertOpen(false);
          }
        }}
      />
    </div>
  );
};

export default WebStoreCatalog;
